import React, { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, LOAN_TYPE_LABEL } from "@/lib/api";
import { toast } from "sonner";
import { Phone, MessageCircle, CalendarClock, X, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["enquiry", "new", "contacted", "interested", "callback", "qualified", "converted", "not_interested", "lost"];

function LeadCard({ l, onStatus, onSchedule }) {
  const [showFu, setShowFu] = useState(false);
  return (
    <div className="bg-white border border-[#E4E4E7] rounded-xl p-5" data-testid={`tele-card-${l.id}`}>
      <div className="flex items-center justify-between">
        <div className="font-mono-num text-xs font-semibold text-[#0052FF]">{l.lead_id}</div>
        <Badge variant="secondary" className="text-[10px]">
          {l.enquiry_type === "insurance" ? (l.insurance_type || "insurance") : (LOAN_TYPE_LABEL[l.loan_type] || l.loan_type || "—")}
        </Badge>
      </div>
      <div className="mt-2 font-display font-bold text-[#06152D] text-lg">{l.full_name}</div>
      <div className="text-sm text-[#52525B] font-mono-num">{l.phone}</div>
      <div className="mt-1 text-xs text-[#52525B]">{l.city || "—"} · Source: {l.source}</div>
      {l.next_followup_at && (
        <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 text-amber-900 inline-flex items-center gap-1">
          <Clock size={12} /> Next: {new Date(l.next_followup_at).toLocaleString("en-IN")}
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <a href={`tel:${l.phone}`} className="flex-1 bg-[#0052FF] hover:bg-[#003FCC] text-white text-xs font-semibold px-3 py-2 rounded-full inline-flex items-center justify-center gap-1" data-testid={`tele-call-${l.id}`}><Phone size={12} /> Call</a>
        <a href={`https://wa.me/${l.phone.replace(/\D/g, "").replace(/^0/, "91").padStart(12, "91")}`} target="_blank" rel="noopener noreferrer" className="flex-1 bg-[#25D366] hover:bg-[#1EBE5A] text-white text-xs font-semibold px-3 py-2 rounded-full inline-flex items-center justify-center gap-1" data-testid={`tele-wa-${l.id}`}><MessageCircle size={12} /> WA</a>
        <button onClick={() => setShowFu(true)} className="flex-1 border border-[#E4E4E7] hover:border-[#0052FF] text-[#06152D] text-xs font-semibold px-3 py-2 rounded-full inline-flex items-center justify-center gap-1" data-testid={`tele-schedule-${l.id}`}><CalendarClock size={12} /> Follow-up</button>
      </div>
      <div className="mt-2">
        <Select value={l.status} onValueChange={(v) => onStatus(l.id, v)}>
          <SelectTrigger className="h-8 text-xs" data-testid={`tele-status-${l.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {showFu && <FollowupDialog lead={l} onClose={() => setShowFu(false)} />}
    </div>
  );
}

function FollowupDialog({ lead, onClose }) {
  const qc = useQueryClient();
  const [when, setWhen] = useState("");
  const [channel, setChannel] = useState("call");
  const [notes, setNotes] = useState("");
  const mut = useMutation({
    mutationFn: async () => (await api.post("/followups", {
      lead_id: lead.id, customer_id: lead.converted_customer_id || null,
      scheduled_at: new Date(when).toISOString(), channel, notes,
    })).data,
    onSuccess: async () => {
      // Sync lead.next_followup_at too
      await api.patch(`/leads/${lead.id}`, { next_followup_at: new Date(when).toISOString() }).catch(() => {});
      toast.success("Follow-up scheduled");
      qc.invalidateQueries(); onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>Schedule Follow-up · {lead.full_name}</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); if (!when) { toast.error("Choose date & time"); return; } mut.mutate(); }} className="space-y-3">
          <div><Label>Date & time</Label><Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="fu-when" /></div>
          <div>
            <Label>Channel</Label>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger data-testid="fu-channel"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="call">Call</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="visit">Visit</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="office_visit">Office Visit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          <button type="submit" disabled={mut.isPending} className="w-full bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-2.5 rounded-full" data-testid="fu-submit">
            {mut.isPending ? "Saving…" : "Schedule"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TelecallerSection({ enquiryType, testidPrefix }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState("pending"); // pending | interested | followup
  const { data: leads = [] } = useQuery({
    queryKey: ["tele-leads", enquiryType, tab],
    queryFn: async () => {
      const params = { enquiry_type: enquiryType, limit: 200 };
      if (tab === "pending") params.status = "enquiry";
      else if (tab === "interested") params.status = "interested";
      const { data } = await api.get("/leads", { params });
      if (tab === "followup") {
        const now = new Date();
        return data.filter((l) => l.next_followup_at && new Date(l.next_followup_at) <= new Date(now.getTime() + 7 * 24 * 3600 * 1000));
      }
      return data;
    },
  });
  const setStatus = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/leads/${id}`, { status })).data,
    onSuccess: () => { toast.success("Status updated"); qc.invalidateQueries(); },
  });

  const tabs = [
    { key: "pending", label: "Pending", count: leads.length && tab === "pending" ? leads.length : null },
    { key: "interested", label: "Interested", count: leads.length && tab === "interested" ? leads.length : null },
    { key: "followup", label: "Follow-ups", count: leads.length && tab === "followup" ? leads.length : null },
  ];

  return (
    <div>
      <div className="flex gap-2 mb-4 border-b border-[#E4E4E7]" data-testid={`${testidPrefix}-tabs`}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === t.key ? "border-[#0052FF] text-[#0052FF]" : "border-transparent text-[#52525B]"}`} data-testid={`${testidPrefix}-tab-${t.key}`}>
            {t.label}{t.count != null ? ` (${t.count})` : ""}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {leads.length === 0 && <div className="text-sm text-[#52525B] col-span-3">No records.</div>}
        {leads.map((l) => (
          <LeadCard key={l.id} l={l} onStatus={(id, status) => setStatus.mutate({ id, status })} />
        ))}
      </div>
    </div>
  );
}

function TeleTabs() {
  const location = useLocation();
  const base = "/admin/telecaller";
  const tabs = [
    { to: base, label: "Leads", exact: true },
    { to: base + "/loans", label: "Loans" },
    { to: base + "/insurance", label: "Insurance" },
  ];
  return (
    <div className="flex gap-2 mb-6 border-b border-[#E4E4E7]" data-testid="tele-top-tabs">
      {tabs.map((t) => (
        <NavLink key={t.to} to={t.to} end={t.exact} className={({ isActive }) => `px-4 py-2 text-sm font-semibold border-b-2 ${isActive ? "border-[#0052FF] text-[#0052FF]" : "border-transparent text-[#52525B]"}`} data-testid={`tele-top-${t.label.toLowerCase()}`}>
          {t.label}
        </NavLink>
      ))}
    </div>
  );
}

export function TelecallerHome() {
  return (
    <div>
      <h1 className="font-display text-3xl font-bold tracking-tight text-[#06152D]">Telecaller Queue</h1>
      <p className="text-sm text-[#52525B]">Work your queue by enquiry type. Move leads through Pending → Interested → Convert.</p>
      <div className="mt-6">
        <TeleTabs />
        <Outlet />
      </div>
    </div>
  );
}

export function TelecallerLeads() {
  return <TelecallerSection enquiryType={undefined} testidPrefix="tele-leads" />;
}

export function TelecallerLoans() {
  return <TelecallerSection enquiryType="loan" testidPrefix="tele-loans" />;
}

export function TelecallerInsurance() {
  return <TelecallerSection enquiryType="insurance" testidPrefix="tele-insurance" />;
}

// Default export = the parent layout with tabs
export default TelecallerHome;
